import React, { useCallback, useContext, useState } from "react";
import { Platform, View } from "react-native";
import styled from "styled-components/native";
import QKModal, { MODAL_CLOSE_DELAY } from "@app/components/modal";
import { Analytics } from "@app/analytics";
import QKScrollView from "@app/components/questkit/ScrollView";
import Button from "@app/components/questkit/button";
import HeaderText from "@app/components/questkit/headerText";
import {
  StandardDropdownListItem,
  StandardInlineDropdown,
} from "@app/components/questkit/dropdown/StandardInlineDropdown";
import { createQuestmateApiClient } from "@app/util/client";
import {
  SnackbarContext,
  SnackbarSeverity,
} from "@app/components/snackbar/SnackbarContext";
import { useAppSelector } from "@app/store";
import { selectLoggedInUser } from "@app/store/cache/users";
import { updateMe } from "@app/util/client/requests/me";

type PageSize = "letter" | "a4";

const PAGE_SIZE_OPTIONS: StandardDropdownListItem<PageSize>[] = [
  { value: "letter", label: "Letter (8.5 x 11 in)" },
  { value: "a4", label: "A4 (210 x 297 mm)" },
];

interface ExportRunModalProps {
  questInstanceId: string;
  showModal: boolean;
  setShowModal: (show: boolean) => void;
}

export const ExportRunModal: React.FC<ExportRunModalProps> = ({
  questInstanceId,
  showModal,
  setShowModal,
}) => {
  return (
    <QKModal
      testID="export-run-modal"
      showModal={showModal}
      setShowModal={setShowModal}
      title="Export Run"
    >
      <ExportRunModalContent
        questInstanceId={questInstanceId}
        onClose={() => setShowModal(false)}
      />
    </QKModal>
  );
};

interface ExportRunModalContentProps {
  questInstanceId: string;
  onClose: () => void;
}

const ExportRunModalContent: React.FC<ExportRunModalContentProps> = ({
  questInstanceId,
  onClose,
}) => {
  const snackbar = useContext(SnackbarContext);
  const [isExporting, setIsExporting] = useState(false);
  const [exportSuccess, setExportSuccess] = useState(false);

  const loggedInUser = useAppSelector(selectLoggedInUser);
  const userDefaultPageSize =
    (loggedInUser?.defaultPdfPageSize as PageSize) || "letter";

  const [pageSize, setPageSize] = useState<PageSize>(userDefaultPageSize);

  const onExport = useCallback(async () => {
    if (isExporting) return;
    setIsExporting(true);

    try {
      const client = createQuestmateApiClient();
      const params = new URLSearchParams();
      params.append("pageSize", pageSize);

      const queryString = params.toString();
      const url = `/questInstances/${questInstanceId}/export/pdf?${queryString}`;

      const response = await client.get(url, {
        responseType: "blob",
      });

      // Trigger download
      if (Platform.OS === "web") {
        const contentDisposition = response.headers["content-disposition"];
        const filenameMatch = contentDisposition?.match(/filename="([^"]+)"/);
        const filename =
          filenameMatch?.[1] ||
          `quest_run_export_${new Date().toISOString().split("T")[0]}.pdf`;

        const blobUrl = window.URL.createObjectURL(response.data);
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(blobUrl);
        a.remove();
      }

      setExportSuccess(true);
      Analytics.trackEvent("Export Quest Run", { format: "pdf" });
      snackbar.sendMessage(
        "Export downloaded successfully",
        SnackbarSeverity.NOTICE
      );
      setTimeout(() => {
        onClose();
      }, MODAL_CLOSE_DELAY);
    } catch (error) {
      console.error("Export failed:", error);
      snackbar.sendMessage("Failed to export run", SnackbarSeverity.WARNING);
    } finally {
      setIsExporting(false);
    }
  }, [isExporting, questInstanceId, pageSize, snackbar, onClose]);

  const preventUserInput = isExporting || exportSuccess;

  return (
    <OptionsDialogWrapper>
      <StyledScrollView keyboardShouldPersistTaps="always">
        <View onStartShouldSetResponder={() => true}>
          <OptionGroup>
            <HeaderText title="Page Size" />
            <StandardInlineDropdown
              value={pageSize}
              options={PAGE_SIZE_OPTIONS}
              optionNoun="page size"
              optionPluralNoun="page sizes"
              loadingOptions={false}
              onSelect={(selection) => {
                if (selection) {
                  setPageSize(selection.value);
                  // Fire and forget - don't block UI for preference save
                  updateMe({ defaultPdfPageSize: selection.value }).catch(
                    (err) => {
                      console.warn("Failed to save page size preference:", err);
                    }
                  );
                }
              }}
              readOnly={preventUserInput}
            />
          </OptionGroup>
        </View>
      </StyledScrollView>
      <ButtonContainer>
        <StyledButton
          title="Export PDF"
          onPress={onExport}
          loading={isExporting}
          success={exportSuccess}
        />
      </ButtonContainer>
    </OptionsDialogWrapper>
  );
};

const OptionsDialogWrapper = styled.View`
  padding-vertical: 10px;
  flex-shrink: 1;
`;

const StyledScrollView = styled(QKScrollView)`
  padding-horizontal: 20px;
`;

const OptionGroup = styled.View`
  margin-bottom: 24px;
`;

const ButtonContainer = styled.View`
  padding-horizontal: 20px;
`;

const StyledButton = styled(Button)`
  width: 100%;
  align-self: center;
  margin-top: 20px;
  margin-bottom: 10px;
`;
